import type {
  ActionSectionItem,
  DropDownNoSelection,
  DropDownRef,
  DropDownResult,
  JsonDataWrapper,
  OdinDataRetrieval,
  OdinDataRetrievalOptions,
} from '@myosh/odin-components';
import {
  DataSearchLocation,
  isDropDownNoSelection,
  type OdinDataSender,
  DropDown as OdinDropDown,
} from '@myosh/odin-components';
import type React from 'react';
import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useGetBowtieByIdQuery, useLazyGetAllBowtiesQuery } from '../../api/enhanced/enhanced-bowtie-api';
import {
  useGetGlobalHierarchyFiltersQuery,
  useGetSimpleRecordQuery,
  useLazyGetSimpleRecordsQuery,
} from '../../api/enhanced/enhanced-v4-api';
import { useConfigurationContext } from '../../context/configuration.context';
import { useDiagramContext } from '../../context/diagram.context';
import type { GlobalHierarchyRecordFilter } from '../../helpers/global-hierarchy';
import { buildGlobalHierarchyRecordFilter } from '../../helpers/global-hierarchy';
import { buildRecordDisplayText, sortRecordsByHierarchy } from '../../helpers/hierarchy-display';
import { cn } from '../../helpers/util';

type DropDownValue = Record<string, unknown>;

const valueFieldKey = 'id';
const textFieldKey = 'displayText';

/**
 * A dropdown component for the header section that displays and manages record selection.
 *
 * @component
 * @description
 * Renders a dropdown menu that allows users to:
 * - Display currently selected record
 * - Search and select records from an API
 * - Open selected records in a new window
 *
 * The component integrates with URL parameters to maintain state and uses the OdinDropDown
 * component for the actual dropdown functionality.
 *
 * @example
 * ```tsx
 * <RecordDropDown />
 * ```
 *
 * @remarks
 * The component handles two main modes:
 * - Regular records mode (when `isSavedDiagram` is false)
 * - Saved diagrams mode (when `isSavedDiagram` is true)
 *
 * @returns {JSX.Element} A dropdown component wrapped in a fixed-width container
 */

const RecordDropDown = (): React.JSX.Element => {
  const { isNewDiagram, isSavedDiagram, recordId, diagramId } = useDiagramContext();

  const [record, setRecord] = useState<DropDownValue>();
  const [showDraft, setShowDraft] = useState<boolean>(isSavedDiagram);

  const showDraftRef = useRef(isSavedDiagram);
  const dropdownRef = useRef<DropDownRef>(null);

  const actionItems: Array<ActionSectionItem> = useMemo(() => {
    return [{ value: 'draft', text: 'Show Draft', selected: showDraft }];
  }, [showDraft]);

  const {
    main: { formId, scenarioFieldId },
  } = useConfigurationContext();

  const [getRecords] = useLazyGetSimpleRecordsQuery();
  const [getAllBowties] = useLazyGetAllBowtiesQuery();

  // The user's saved global hierarchy scope (set in the web app) is applied to the picker so it lists
  // only bowties within that hierarchy. Held in a ref so the memoized data retrieval reads the latest value.
  const { data: globalHierarchyData, isLoading: isGlobalHierarchyLoading } = useGetGlobalHierarchyFiltersQuery();
  const globalHierarchyFilterRef = useRef<GlobalHierarchyRecordFilter>({});
  // Whether the saved scope query has resolved (success or error). The picker defers its first record
  // fetch until then so the initial request already carries the hierarchy filter — avoiding a wasted
  // unscoped fetch (the heaviest query, no hierarchy filter) immediately followed by a scoped refetch.
  const isGlobalHierarchySettledRef = useRef(false);

  const { data, isFetching } = useGetSimpleRecordQuery(
    { id: Number(recordId), params: {} },
    { skip: !recordId || isNewDiagram || showDraft }
  );

  const { data: diagram, isFetching: isDiagramFetching } = useGetBowtieByIdQuery(
    { bowtieId: diagramId! },
    { skip: !diagramId || isNewDiagram }
  );

  if (!isFetching && data && !record && !showDraft) {
    // For the value to be set on the drop down, it must be in the format of DropDownResult and have 'value' and 'text' properties
    setRecord({ ...data, value: data.id, text: data.fields?.[scenarioFieldId] });
  }

  if (!isDiagramFetching && diagram && !record && showDraft) {
    // For the value to be set on the drop down, it must be in the format of DropDownResult and have 'value' and 'text' properties
    setRecord({ ...diagram, value: diagram.id, text: diagram.risk });
  }

  useEffect(() => {
    // Draft mode lists saved diagrams and doesn't depend on the hierarchy scope, so reload it
    // immediately — don't make a mode toggle wait for the scope query to settle.
    if (showDraft) {
      dropdownRef.current?.getDataOverwritePageCache();
      return;
    }
    // Records mode: wait for the saved scope to resolve before (re)loading, so the picker never fires
    // an unscoped fetch first and then a scoped refetch — it loads once, already scoped.
    if (isGlobalHierarchyLoading) {
      return;
    }
    globalHierarchyFilterRef.current = buildGlobalHierarchyRecordFilter(globalHierarchyData);
    isGlobalHierarchySettledRef.current = true;
    // runs in an effect to ensure dropdown state is updated when the mode or hierarchy scope changes
    dropdownRef.current?.getDataOverwritePageCache();
  }, [showDraft, globalHierarchyData, isGlobalHierarchyLoading]);

  const dataRetrieval: OdinDataRetrieval = useMemo(() => {
    return {
      getData: async (subscriber: OdinDataSender<JsonDataWrapper>, options?: OdinDataRetrievalOptions) => {
        if (options) {
          if (showDraftRef.current === true) {
            if (options.page === 1) {
              // load list of saved diagrams only
              const diagrams = await getAllBowties().unwrap();

              const data =
                diagrams
                  .map((diagram) => ({
                    ...diagram,
                    [valueFieldKey]: diagram.id,
                    [textFieldKey]: diagram.risk,
                  }))
                  .filter((diagram) => {
                    // workaround to filter out the data manually as the API does not support filtering
                    if (options.fieldFilters?.[textFieldKey]) {
                      return diagram[textFieldKey]
                        ?.toLocaleLowerCase()
                        .includes((options.fieldFilters[textFieldKey].value as string).toLowerCase());
                    }

                    return true;
                  }) ?? [];

              subscriber.sendData({ data });
            } else {
              subscriber.sendData();
            }
          } else {
            // Defer the first load until the saved hierarchy scope has resolved, so the request is
            // already scoped rather than firing an unscoped (and slowest) fetch that is then refetched.
            // The scope-resolution effect re-triggers this load via getDataOverwritePageCache().
            if (!isGlobalHierarchySettledRef.current) {
              subscriber.sendData();
              return;
            }

            let filter;
            if (options.fieldFilters?.[textFieldKey]) {
              filter = `field:${scenarioFieldId}:like:${options.fieldFilters[textFieldKey].value}`;
            }

            const { filter: globalHierarchyFilters, match: globalHierarchyMatch } = globalHierarchyFilterRef.current;
            const records = await getRecords({
              formId,
              page: options.page ?? 1,
              pageSize: options.pageSize ?? 50,
              sortDirective: [`field:${scenarioFieldId}`],
              filter,
              globalHierarchyFilters,
              globalHierarchyMatch,
            }).unwrap();

            // Only surface the hierarchy (prefix + cluster the page) when the list is actually scoped by
            // a hierarchy filter; otherwise keep the plain scenario list, sorted by scenario as before.
            const isHierarchyFiltered = Boolean(globalHierarchyFilters);
            const orderedRecords = isHierarchyFiltered ? sortRecordsByHierarchy(records, scenarioFieldId) : records;
            const data =
              orderedRecords.map((record) => ({
                ...record,
                [textFieldKey]: isHierarchyFiltered
                  ? buildRecordDisplayText(record, scenarioFieldId)
                  : String(record.fields?.[scenarioFieldId] ?? ''),
              })) ?? [];
            subscriber.sendData({ data });
          }
        } else {
          subscriber.sendData();
        }
      },
    };
    // The hierarchy scope + draft mode are read via refs inside getData, so they intentionally don't
    // re-create this; the config ids and lazy-query triggers are the real dependencies.
  }, [formId, scenarioFieldId, getAllBowties, getRecords]);

  const handleOnChange = (value?: DropDownResult | Array<DropDownResult> | DropDownNoSelection) => {
    if (value && !isDropDownNoSelection(value) && !Array.isArray(value)) {
      if (showDraftRef.current) {
        const params = new URLSearchParams({ diagramId: String(value.value) });
        window.open(`${window.location.origin}${window.location.pathname}?${params}`);
      } else {
        const params = new URLSearchParams({ records: String(value.value) });
        window.open(`${window.location.origin}${window.location.pathname}?${params}`);
      }

      // this is a workaround as the drop down will set the selected value internally,
      // and we need to keep the previous value
      if (isNewDiagram) {
        // clear selection for new diagrams
        setRecord({ noSelectionValue: true });
      } else {
        // reset selection for existing diagrams
        setRecord({ ...record });
      }
    }
  };

  const handleChangeActionItem = useCallback((item: ActionSectionItem) => {
    if (item.value === 'draft') {
      showDraftRef.current = Boolean(item.selected);
      setShowDraft(Boolean(item.selected));
    }
  }, []);

  const selectorStyles = cn('text-base font-bold leading-9', {
    "before:content-['New_Diagram']": isNewDiagram,
  });

  return (
    <div data-cy="record-dropdown-wrapper" className="max-w-80 min-w-80">
      <OdinDropDown
        data-cy="record-dropdown"
        ref={dropdownRef}
        valueField={valueFieldKey}
        textField={textFieldKey}
        value={record as unknown as DropDownResult | DropDownNoSelection}
        onChange={handleOnChange}
        data={dataRetrieval}
        filterLocation={DataSearchLocation.Api}
        allowClear={false}
        preloadData
        shouldUseCreatePortal
        className="bg-gray-5! h-12 rounded-sm"
        selectorStyles={selectorStyles}
        actionItems={actionItems}
        onActionItemChanged={handleChangeActionItem}
      />
    </div>
  );
};

export default memo(RecordDropDown);
